import { useGetSearchOptionFromUrl } from '@/hooks/useGetSearchOptionFromUrl';
import { useSearchOptions } from '@/hooks/useSearchOptions';
import SearchLoading from '@/pages/search/components/SearchLoading';
import { ContentWrapper } from '@/pages/search/components/styles';
import TimeoutModal from '@/pages/search/components/TimeoutModal';
import SearchNull from '@/pages/search/components/vehicleCard/SearchNull';
import MobileSearchResult from '@/pages/search/mobile/components/MobileSearchResult';
import {
  CAR_TYPE,
  cn,
  convertToKST,
  filterInsuranceAtom,
  filterOptionAtom,
  getFilterSearchResult,
  getSureUserId,
  PAGE_NAME,
  SearchParamsType,
  TabList,
  useGetVehicleModels,
  useRecoil,
} from '@orcar/common-d2c';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MobileOptionSearchModal } from '@/pages/home/components/searchModal/MobileOptionSearchModal';
import { sortAvailableFirst } from '@/utils/vehicle-model';
import MobileFilter from './components/filter';

const MobileSearchLayout = () => {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const filterModal = searchParams.get('filterModal') === 'true';
  const searchOptionModal = searchParams.get('searchOptionModal') === 'true';
  const sureUserId = getSureUserId();

  const { recoilValue: recoilInsurance } = useRecoil(filterInsuranceAtom);
  const { recoilValue: recoilFilterOption } = useRecoil(filterOptionAtom);

  const { initialSearchOptions } = useGetSearchOptionFromUrl();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const {
    minPrice,
    maxPrice,
    minYear,
    maxYear,
    carFuelList,
    carTypeList,
    optionList,
  } = recoilFilterOption;

  const hasMinPriceFilter = minPrice !== null;
  const hasMaxPriceFilter = maxPrice !== null;
  const hasMinYearFilter = minYear !== null;
  const hasMaxYearFilter = maxYear !== null;

  const isFilterApplied =
    !!hasMinPriceFilter ||
    !!hasMaxPriceFilter ||
    !!hasMaxYearFilter ||
    !!hasMinYearFilter ||
    !!carTypeList.length ||
    !!carFuelList.length ||
    !!optionList.length ||
    !!recoilInsurance.length;

  const {
    data: vehicleModelList,
    isLoading,
    isError,
    error,
  } = useGetVehicleModels({
    params: {
      pickUpAt: convertToKST(initialSearchOptions.pickUpAt),
      dropOffAt: convertToKST(initialSearchOptions.dropOffAt),
      driverAge: initialSearchOptions.driverAge,
      numberOfPeople: initialSearchOptions.numberOfPeople,
      drivingExperience: initialSearchOptions.drivingExperience,
      sureUserId: sureUserId ? Number(sureUserId) : undefined,
    },
  });

  // 예약 가능한 차량을 먼저, 예약마감 차량은 뒤로(마감 표시는 카드에서 유지).
  const filterResult = sortAvailableFirst(
    getFilterSearchResult(
      vehicleModelList,
      recoilFilterOption,
      recoilInsurance,
    ),
  );

  const handleOpenModal = (modalType: 'filter' | 'searchOption') => {
    setSearchParams((params) => {
      if (modalType === 'filter') params.set('filterModal', 'true');
      if (modalType === 'searchOption') params.set('searchOptionModal', 'true');
      return params;
    });
  };

  const handleCloseModal = (modalType: string, onRouterBack = true) => {
    document.body.style.overflow = 'auto';

    if (onRouterBack) {
      navigate(-1);
    }
  };

  const handleSubmitSearch = (params?: SearchParamsType) => {
    navigate({
      pathname: PAGE_NAME.search,
      search: new URLSearchParams({
        pickUpAt: convertToKST(params?.pickUpAt),
        dropOffAt: convertToKST(params?.dropOffAt),
        driverAge: params?.driverAge || '',
        drivingExperience: params?.drivingExperience || '',
        numberOfPeople: params?.numberOfPeople || '',
      }).toString(),
    });
  };

  const {
    searchOptions,
    searchOptionIdx,
    setSearchOptionIdx,
    isNextBtnDisabled,
    isCheckTimeSelected,
    handleNextButton,
    handleTabButton,
  } = useSearchOptions({
    onSubmit: handleSubmitSearch,
    onModalClose: () => handleCloseModal('searchOption', false),
    initialSearchOptions,
  });

  useEffect(() => {
    setIsFilterOpen(filterModal);
  }, [filterModal]);

  useEffect(() => {
    setIsSearchModalOpen(searchOptionModal);
  }, [searchOptionModal]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsModalOpen(true);
    }, 900000);

    return () => {
      clearTimeout(timer);
    };
  }, [isModalOpen]);

  return (
    <div className='size-full p-5 pt-[146px]'>
      <div className='fixed left-0 top-[66px] z-[9] flex h-fit w-full flex-col bg-grey-000 pt-5 shadow-050'>
        <div className='flex overflow-x-auto pl-5'>
          <TabList tabList={CAR_TYPE} />
        </div>
        <div className='flex min-w-[400px] items-center gap-2 px-5 py-3'>
          <div
            className={cn(
              'flex h-9 cursor-pointer items-center justify-center rounded-s border border-primary-050 px-2',
              isFilterApplied && 'bg-primary-050',
            )}
            onClick={() => handleOpenModal('filter')}
          >
            {isFilterApplied ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='18'
                height='20'
                viewBox='0 0 18 20'
                fill='none'
              >
                <path
                  d='M12.0999 17C11.7405 17 11.408 16.9124 11.1025 16.7373C10.797 16.5681 10.5544 16.3346 10.3747 16.0369C10.201 15.7392 10.1142 15.4153 10.1142 15.065C10.1142 14.7031 10.201 14.3763 10.3747 14.0844C10.5544 13.7926 10.797 13.562 11.1025 13.3927C11.408 13.2176 11.7405 13.1301 12.0999 13.1301C12.4653 13.1301 12.7977 13.2176 13.0973 13.3927C13.4027 13.562 13.6424 13.7926 13.8161 14.0844C13.9958 14.3763 14.0856 14.7031 14.0856 15.065C14.0856 15.4153 13.9958 15.7392 13.8161 16.0369C13.6424 16.3346 13.4027 16.5681 13.0973 16.7373C12.7977 16.9124 12.4653 17 12.0999 17ZM12.0999 16.0544C12.3874 16.0544 12.627 15.9581 12.8187 15.7655C13.0164 15.5729 13.1152 15.3394 13.1152 15.065C13.1152 14.7849 13.0164 14.5514 12.8187 14.3646C12.627 14.172 12.3874 14.0757 12.0999 14.0757C11.8184 14.0757 11.5788 14.172 11.3811 14.3646C11.1834 14.5514 11.0846 14.7849 11.0846 15.065C11.0846 15.3394 11.1834 15.5729 11.3811 15.7655C11.5788 15.9581 11.8184 16.0544 12.0999 16.0544ZM10.7431 14.4522V15.6692H1.11998C0.946265 15.6692 0.799507 15.6108 0.679704 15.4941C0.559901 15.3715 0.5 15.2285 0.5 15.065C0.5 14.8958 0.559901 14.7528 0.679704 14.636C0.799507 14.5134 0.946265 14.4522 1.11998 14.4522H10.7431ZM16.907 14.4522C17.0687 14.4522 17.2065 14.5134 17.3203 14.636C17.4401 14.7528 17.5 14.8958 17.5 15.065C17.5 15.2285 17.4401 15.3715 17.3203 15.4941C17.2065 15.6108 17.0687 15.6692 16.907 15.6692H13.5645V14.4522H16.907ZM6.04387 11.9393C5.67847 11.9393 5.34302 11.8547 5.03753 11.6854C4.73802 11.5103 4.49841 11.2768 4.31871 10.985C4.13901 10.6873 4.04915 10.3604 4.04915 10.0044C4.04915 9.64832 4.13901 9.32437 4.31871 9.03252C4.49841 8.73483 4.73802 8.50136 5.03753 8.33208C5.34302 8.15697 5.67847 8.06942 6.04387 8.06942C6.40328 8.06942 6.73273 8.15697 7.03224 8.33208C7.33774 8.50136 7.58034 8.73483 7.76004 9.03252C7.93975 9.32437 8.0296 9.64832 8.0296 10.0044C8.0296 10.3604 7.93975 10.6873 7.76004 10.985C7.58034 11.2768 7.33774 11.5103 7.03224 11.6854C6.73273 11.8547 6.40328 11.9393 6.04387 11.9393ZM6.04387 10.9937C6.3314 10.9937 6.571 10.8974 6.76268 10.7048C6.95437 10.5122 7.05021 10.2787 7.05021 10.0044C7.05021 9.7242 6.95437 9.49072 6.76268 9.30394C6.571 9.11132 6.3314 9.01501 6.04387 9.01501C5.75634 9.01501 5.51374 9.11132 5.31607 9.30394C5.12438 9.49072 5.02854 9.7242 5.02854 10.0044C5.02854 10.2787 5.12438 10.5122 5.31607 10.7048C5.51374 10.8974 5.75634 10.9937 6.04387 10.9937ZM1.09302 9.40025H4.59725V10.6085H1.09302C0.93129 10.6085 0.790522 10.5501 0.670719 10.4334C0.556906 10.3167 0.5 10.1737 0.5 10.0044C0.5 9.83511 0.556906 9.6921 0.670719 9.57536C0.790522 9.45862 0.93129 9.40025 1.09302 9.40025ZM16.88 9.40025C17.0537 9.40025 17.2005 9.45862 17.3203 9.57536C17.4401 9.6921 17.5 9.83511 17.5 10.0044C17.5 10.1737 17.4401 10.3167 17.3203 10.4334C17.2005 10.5501 17.0537 10.6085 16.88 10.6085H7.39165V9.40025H16.88ZM12.0999 6.87867C11.7405 6.87867 11.408 6.79112 11.1025 6.61601C10.797 6.43506 10.5544 6.19867 10.3747 5.90682C10.201 5.61497 10.1142 5.29393 10.1142 4.94371C10.1142 4.58182 10.201 4.25495 10.3747 3.9631C10.5544 3.67125 10.797 3.43777 11.1025 3.26266C11.408 3.08755 11.7405 3 12.0999 3C12.4653 3 12.7977 3.08755 13.0973 3.26266C13.4027 3.43777 13.6424 3.67125 13.8161 3.9631C13.9958 4.25495 14.0856 4.58182 14.0856 4.94371C14.0856 5.29393 13.9958 5.61497 13.8161 5.90682C13.6424 6.19867 13.4027 6.43506 13.0973 6.61601C12.7977 6.79112 12.4653 6.87867 12.0999 6.87867ZM12.0999 5.92433C12.3874 5.92433 12.627 5.82802 12.8187 5.6354C13.0164 5.44278 13.1152 5.2093 13.1152 4.93496C13.1152 4.65478 13.0164 4.42131 12.8187 4.23452C12.627 4.0419 12.3874 3.94559 12.0999 3.94559C11.8184 3.94559 11.5788 4.0419 11.3811 4.23452C11.1834 4.42131 11.0846 4.65478 11.0846 4.93496C11.0846 5.2093 11.1834 5.44278 11.3811 5.6354C11.5788 5.82802 11.8184 5.92433 12.0999 5.92433ZM10.7791 4.33959V5.54784H1.11998C0.946265 5.54784 0.799507 5.48947 0.679704 5.37273C0.559901 5.25599 0.5 5.11299 0.5 4.94371C0.5 4.77444 0.559901 4.63144 0.679704 4.5147C0.799507 4.39796 0.946265 4.33959 1.11998 4.33959H10.7791ZM16.907 4.33959C17.0687 4.33959 17.2065 4.39796 17.3203 4.5147C17.4401 4.63144 17.5 4.77444 17.5 4.94371C17.5 5.11299 17.4401 5.25599 17.3203 5.37273C17.2065 5.48947 17.0687 5.54784 16.907 5.54784H13.4746V4.33959H16.907Z'
                  fill='white'
                />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='18'
                height='20'
                viewBox='0 0 18 20'
                fill='none'
                className='fill-primary-050'
              >
                <path d='M12.0999 17C11.7405 17 11.408 16.9124 11.1025 16.7373C10.797 16.5681 10.5544 16.3346 10.3747 16.0369C10.201 15.7392 10.1142 15.4153 10.1142 15.065C10.1142 14.7031 10.201 14.3763 10.3747 14.0844C10.5544 13.7926 10.797 13.562 11.1025 13.3927C11.408 13.2176 11.7405 13.1301 12.0999 13.1301C12.4653 13.1301 12.7977 13.2176 13.0973 13.3927C13.4027 13.562 13.6424 13.7926 13.8161 14.0844C13.9958 14.3763 14.0856 14.7031 14.0856 15.065C14.0856 15.4153 13.9958 15.7392 13.8161 16.0369C13.6424 16.3346 13.4027 16.5681 13.0973 16.7373C12.7977 16.9124 12.4653 17 12.0999 17ZM12.0999 16.0544C12.3874 16.0544 12.627 15.9581 12.8187 15.7655C13.0164 15.5729 13.1152 15.3394 13.1152 15.065C13.1152 14.7849 13.0164 14.5514 12.8187 14.3646C12.627 14.172 12.3874 14.0757 12.0999 14.0757C11.8184 14.0757 11.5788 14.172 11.3811 14.3646C11.1834 14.5514 11.0846 14.7849 11.0846 15.065C11.0846 15.3394 11.1834 15.5729 11.3811 15.7655C11.5788 15.9581 11.8184 16.0544 12.0999 16.0544ZM10.7431 14.4522V15.6692H1.11998C0.946265 15.6692 0.799507 15.6108 0.679704 15.4941C0.559901 15.3715 0.5 15.2285 0.5 15.065C0.5 14.8958 0.559901 14.7528 0.679704 14.636C0.799507 14.5134 0.946265 14.4522 1.11998 14.4522H10.7431ZM16.907 14.4522C17.0687 14.4522 17.2065 14.5134 17.3203 14.636C17.4401 14.7528 17.5 14.8958 17.5 15.065C17.5 15.2285 17.4401 15.3715 17.3203 15.4941C17.2065 15.6108 17.0687 15.6692 16.907 15.6692H13.5645V14.4522H16.907ZM6.04387 11.9393C5.67847 11.9393 5.34302 11.8547 5.03753 11.6854C4.73802 11.5103 4.49841 11.2768 4.31871 10.985C4.13901 10.6873 4.04915 10.3604 4.04915 10.0044C4.04915 9.64832 4.13901 9.32437 4.31871 9.03252C4.49841 8.73483 4.73802 8.50136 5.03753 8.33208C5.34302 8.15697 5.67847 8.06942 6.04387 8.06942C6.40328 8.06942 6.73273 8.15697 7.03224 8.33208C7.33774 8.50136 7.58034 8.73483 7.76004 9.03252C7.93975 9.32437 8.0296 9.64832 8.0296 10.0044C8.0296 10.3604 7.93975 10.6873 7.76004 10.985C7.58034 11.2768 7.33774 11.5103 7.03224 11.6854C6.73273 11.8547 6.40328 11.9393 6.04387 11.9393ZM6.04387 10.9937C6.3314 10.9937 6.571 10.8974 6.76268 10.7048C6.95437 10.5122 7.05021 10.2787 7.05021 10.0044C7.05021 9.7242 6.95437 9.49072 6.76268 9.30394C6.571 9.11132 6.3314 9.01501 6.04387 9.01501C5.75634 9.01501 5.51374 9.11132 5.31607 9.30394C5.12438 9.49072 5.02854 9.7242 5.02854 10.0044C5.02854 10.2787 5.12438 10.5122 5.31607 10.7048C5.51374 10.8974 5.75634 10.9937 6.04387 10.9937ZM1.09302 9.40025H4.59725V10.6085H1.09302C0.93129 10.6085 0.790522 10.5501 0.670719 10.4334C0.556906 10.3167 0.5 10.1737 0.5 10.0044C0.5 9.83511 0.556906 9.6921 0.670719 9.57536C0.790522 9.45862 0.93129 9.40025 1.09302 9.40025ZM16.88 9.40025C17.0537 9.40025 17.2005 9.45862 17.3203 9.57536C17.4401 9.6921 17.5 9.83511 17.5 10.0044C17.5 10.1737 17.4401 10.3167 17.3203 10.4334C17.2005 10.5501 17.0537 10.6085 16.88 10.6085H7.39165V9.40025H16.88ZM12.0999 6.87867C11.7405 6.87867 11.408 6.79112 11.1025 6.61601C10.797 6.43506 10.5544 6.19867 10.3747 5.90682C10.201 5.61497 10.1142 5.29393 10.1142 4.94371C10.1142 4.58182 10.201 4.25495 10.3747 3.9631C10.5544 3.67125 10.797 3.43777 11.1025 3.26266C11.408 3.08755 11.7405 3 12.0999 3C12.4653 3 12.7977 3.08755 13.0973 3.26266C13.4027 3.43777 13.6424 3.67125 13.8161 3.9631C13.9958 4.25495 14.0856 4.58182 14.0856 4.94371C14.0856 5.29393 13.9958 5.61497 13.8161 5.90682C13.6424 6.19867 13.4027 6.43506 13.0973 6.61601C12.7977 6.79112 12.4653 6.87867 12.0999 6.87867ZM12.0999 5.92433C12.3874 5.92433 12.627 5.82802 12.8187 5.6354C13.0164 5.44278 13.1152 5.2093 13.1152 4.93496C13.1152 4.65478 13.0164 4.42131 12.8187 4.23452C12.627 4.0419 12.3874 3.94559 12.0999 3.94559C11.8184 3.94559 11.5788 4.0419 11.3811 4.23452C11.1834 4.42131 11.0846 4.65478 11.0846 4.93496C11.0846 5.2093 11.1834 5.44278 11.3811 5.6354C11.5788 5.82802 11.8184 5.92433 12.0999 5.92433ZM10.7791 4.33959V5.54784H1.11998C0.946265 5.54784 0.799507 5.48947 0.679704 5.37273C0.559901 5.25599 0.5 5.11299 0.5 4.94371C0.5 4.77444 0.559901 4.63144 0.679704 4.5147C0.799507 4.39796 0.946265 4.33959 1.11998 4.33959H10.7791ZM16.907 4.33959C17.0687 4.33959 17.2065 4.39796 17.3203 4.5147C17.4401 4.63144 17.5 4.77444 17.5 4.94371C17.5 5.11299 17.4401 5.25599 17.3203 5.37273C17.2065 5.48947 17.0687 5.54784 16.907 5.54784H13.4746V4.33959H16.907Z' />
              </svg>
            )}
          </div>
          <div className='flex min-w-[320px] flex-1 items-center justify-between gap-2 rounded-s bg-grey-005 px-3 py-2'>
            <div className='caption1 tracking-xl text-grey-070'>
              {`${dayjs(initialSearchOptions.pickUpAt).format(
                'YY.MM.DD (ddd) HH:mm',
              )} ~ ${dayjs(initialSearchOptions.dropOffAt).format(
                'YY.MM.DD (ddd) HH:mm',
              )}`}
            </div>
            <button
              className='body2 flex items-center rounded-s bg-primary-050 px-2 py-1 text-grey-000'
              onClick={() => handleOpenModal('searchOption')}
            >
              일정변경
            </button>
          </div>
        </div>
      </div>
      <ContentWrapper>
        {isError ? (
          <SearchNull
            error={error}
            onMobileBottomSheetOpen={() => handleOpenModal('searchOption')}
          />
        ) : isLoading ? (
          <SearchLoading />
        ) : (
          <MobileSearchResult
            searchOptions={initialSearchOptions}
            vehicleModelList={vehicleModelList}
            filterResult={filterResult}
          />
        )}
      </ContentWrapper>
      {isModalOpen && (
        <TimeoutModal onClose={() => setIsModalOpen(false)} isMobile />
      )}
      {isSearchModalOpen && (
        <MobileOptionSearchModal
          showModal={isSearchModalOpen}
          onClose={() => handleCloseModal('searchOption', true)}
          searchOptionIdx={searchOptionIdx}
          setSearchOptionIdx={setSearchOptionIdx}
          searchOptions={searchOptions}
          isNextBtnDisabled={isNextBtnDisabled}
          isCheckTimeSelected={isCheckTimeSelected}
          handleNextBtn={handleNextButton}
          handleTabBtn={handleTabButton}
        />
      )}
      {isFilterOpen && (
        <MobileFilter
          onClose={() => handleCloseModal('filter')}
          showModal={isFilterOpen}
          vehicleModelList={vehicleModelList}
        />
      )}
    </div>
  );
};

export default MobileSearchLayout;
